// Login page (/login), after the "BeyondAge ERPNext Desk" design: a brand panel on the left,
// Frappe's own login form on the right.
//
// Loaded on every website page through `web_include_js`, so it does nothing unless the page is
// /login. Frappe's template is not overridden — password, forgot-password, email-link, social
// (Login with Google appears once a Google Social Login Key is enabled), LDAP and 2FA flows all
// stay stock; this only adds the panel with the animated BA Breath Logo, a heading, visible labels
// and a theme switch around them. Styled by ba_login.bundle.css.
//
// Theme: /login has no Frappe theme of its own and a Guest has no preference, so the page opens in
// the theme the desk last showed and a switch here is saved to the user's Frappe theme preference
// on the first desk load after login (shared/ba_theme_sync.js, desk/theme_sync.js).

import { BeyondageRings, BA_BREATH_LOGO_CONFIG } from "./desk/ba_breath_logo.js";
import { read_theme, mark_login_choice } from "./shared/ba_theme_sync.js";

// Same BA Breath Logo as the CRM and desk sidebars: loops breathe, corner dots pulse opposite.
const LOGIN_RINGS = BA_BREATH_LOGO_CONFIG;

(function () {
	const MOON = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
		stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z"/></svg>`;
	const SUN = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
		stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4"/></svg>`;
	const LINK = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
		stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1"/><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1"/></svg>`;

	const t = (s, args) => (window.__ ? window.__(s, args) : s);

	const storage = (() => {
		try {
			return window.localStorage;
		} catch (e) {
			return null;
		}
	})();

	function apply_theme(theme, btn) {
		// data-theme is Frappe's own attribute; data-ba-login-theme drives ba_login.bundle.css
		document.documentElement.setAttribute("data-theme", theme);
		document.documentElement.setAttribute("data-ba-login-theme", theme);
		if (btn) {
			const dark = theme === "dark";
			// icon only (sun in dark, moon in light); the action is its accessible name and tooltip
			const label = dark ? t("Switch to Light Theme") : t("Switch to Dark Theme");
			btn.innerHTML = dark ? SUN : MOON;
			btn.setAttribute("aria-label", label);
			btn.setAttribute("title", label);
		}
	}

	function esc(s) {
		const d = document.createElement("div");
		d.textContent = s == null ? "" : String(s);
		return d.innerHTML;
	}

	function build() {
		const page = document.getElementById("page-login");
		if (!page || page.querySelector(".ba-login-panel")) return;
		document.body.classList.add("ba-login");

		const logo = page.querySelector(".for-login .app-logo");
		const footer = document.querySelector(".web-footer .footer-info .footer-col-left");
		const copyright =
			(footer && footer.textContent.trim()) ||
			t("© {0} BeyondAge. All rights reserved.", [new Date().getFullYear()]);

		const panel = document.createElement("aside");
		panel.className = "ba-login-panel";
		panel.innerHTML = `
			<div class="ba-login-panel__brand">
				${logo ? `<img src="${esc(logo.getAttribute("src"))}" alt="${esc(t("BeyondAge"))}">` : ""}
			</div>
			<div class="ba-login-panel__art" aria-hidden="true"><canvas></canvas></div>
			<div class="ba-login-panel__copy">
				<div class="ba-login-panel__kicker">${esc(t("Operations Desk"))}</div>
				<div class="ba-login-panel__title">${esc(
					t("Everything our team needs to run BeyondAge, in one calm place.")
				)}</div>
				<p class="ba-login-panel__text">${esc(t("The entire ERP suite, one sign-in away."))}</p>
			</div>
			<div class="ba-login-panel__foot">${esc(copyright)}</div>`;
		page.prepend(panel);
		start_rings(panel.querySelector(".ba-login-panel__art canvas"));

		const wrapper = page.querySelector(".page-content-wrapper");
		if (wrapper && !wrapper.querySelector(".ba-login-theme")) {
			const bar = document.createElement("div");
			bar.className = "ba-login-bar";
			const btn = document.createElement("button");
			btn.type = "button";
			btn.className = "ba-login-theme";
			bar.appendChild(btn);
			wrapper.prepend(bar);
			apply_theme(read_theme(storage), btn);
			btn.addEventListener("click", () => {
				const next =
					document.documentElement.getAttribute("data-ba-login-theme") === "dark" ? "light" : "dark";
				mark_login_choice(storage, next);
				apply_theme(next, btn);
			});
		}

		// "Login to <site>" becomes the kicker over a real page heading.
		const head = page.querySelector(".for-login .page-card-head");
		if (head && !head.querySelector(".ba-login-title")) {
			head.insertAdjacentHTML("beforeend", `<h1 class="ba-login-title">${esc(t("Welcome back"))}</h1>`);
		}

		// Frappe ships the labels as sr-only; the design shows them.
		page.querySelectorAll(".for-login .form-label.sr-only").forEach((l) => l.classList.remove("sr-only"));

		// Forgot Password? sits on the Password label's row.
		const pw_group = page.querySelector(".for-login .password-field");
		const forgot = page.querySelector(".for-login .forgot-password-message");
		const pw_label = page.querySelector('.for-login label[for="login_password"]');
		if (pw_group && forgot && pw_label && !page.querySelector(".ba-login-label-row")) {
			const row = document.createElement("div");
			row.className = "ba-login-label-row";
			pw_label.parentNode.insertBefore(row, pw_label);
			row.append(pw_label, forgot);
		}

		const email_link = page.querySelector(".btn-login-with-email-link");
		if (email_link && !email_link.querySelector("svg")) email_link.insertAdjacentHTML("afterbegin", LINK);
	}

	function start_rings(canvas) {
		if (!canvas) return;
		const rings = BeyondageRings(canvas, LOGIN_RINGS);
		const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)");
		const sync = () => (reduce && reduce.matches ? rings.pause() : rings.play());
		sync();
		if (reduce && reduce.addEventListener) reduce.addEventListener("change", sync);
	}

	// This bundle and ba_login.bundle.css load on every website page, so the login fonts are
	// requested from here, on /login only, instead of a render-blocking CSS @import.
	const FONTS =
		"https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=Montserrat:wght@400;500;600;700&display=swap";
	function load_fonts() {
		const link = (rel, href, cross) => {
			const l = document.createElement("link");
			l.rel = rel;
			l.href = href;
			if (cross) l.crossOrigin = "";
			document.head.appendChild(l);
		};
		link("preconnect", "https://fonts.googleapis.com");
		link("preconnect", "https://fonts.gstatic.com", true);
		link("stylesheet", FONTS);
	}

	function start() {
		if (document.body.dataset.path !== "login") return;
		load_fonts();
		apply_theme(read_theme(storage));
		build();
	}

	if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
	else start();
})();
